<template>
  <section class="section">
    <b-loading v-if="isLoading" :is-full-page="true"/>

    <div v-else class="container">
      <div class="level">
        <div class="level-left">
          <span>{{ $t('customer.scan_stock.total_quantity') }}: {{ totalStockQuantity }}</span>
        </div>
        <div class="level-right">
          <b-button type="is-primary" :disabled="!hasStock" @click="exportScanStock">
            {{ $t('global.export') }}
          </b-button>
        </div>
      </div>

      <ag-grid-vue
        style="width: 100%; height: 1000px;"
        class="ag-theme-alpine"
        :modules="modules"
        :pagination="true"
        :pagination-auto-page-size="true"
        :group-display-type="groupDisplayType"
        :grid-options="gridOptions"
        :row-data="stock"
        :column-defs="columnDefs"
      />
    </div>
  </section>
</template>

<script>
import { mapState } from 'vuex'
import { AllModules } from '@ag-grid-enterprise/all-modules'
import XLSX from 'xlsx'
import Enduser from '@/services/v2/enduser'

export default {
  data () {
    return {
      modules: AllModules,
      groupDisplayType: 'custom',
      isLoading: false,
      stock: []
    }
  },
  computed: {
    ...mapState({
      selectedCustomer: state => state.selectedCustomer
    }),
    gridOptions () {
      return {
        autoGroupColumnDef: {
          headerName: this.$t('customer.scan_stock.grid.date'),
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        },
        getContextMenuItems: this.getContextMenuItems
      }
    },
    columnDefs () {
      return [
        { headerName: this.$t('customer.scan_stock.grid.date'),
          field: 'date',
          valueFormatter: (params) => {
            if (!params.value) return ''
            return params.value.split('T')[0]
          },
          keyCreator: (params) => this.formatExportValue(params.value, 'date'),
          filter: 'agDateColumnFilter',
          filterParams: {
            comparator: this.compareDateFilter,
            suppressAndOrCondition: true
          },
          rowGroup: true,
          hide: true
        },
        { headerName: this.$t('customer.scan_stock.grid.barcode'),
          field: 'barcode',
          filter: 'agTextColumnFilter',
          filterParams: { suppressAndOrCondition: true }
        }
      ]
    },
    hasStock () {
      return !!(this.stock && this.stock.length)
    },
    totalStockQuantity () {
      return this.stock ? this.stock.length : 0
    }
  },
  watch: {
    selectedCustomer: {
      immediate: true,
      handler (newValue, oldValue) {
        if (newValue) {
          this.getStock()
        }
      }
    }
  },
  async mounted () {
  },
  methods: {
    async getStock () {
      if (!this.selectedCustomer) return
      this.isLoading = true
      try {
        const response = await Enduser.getScanStock(this.selectedCustomer)
        this.stock = response || []
      } catch (error) {
        this.$buefy.toast.open({
          message: this.$t('customer.scan_stock.errors.load_data'),
          type: 'is-danger',
          duration: 5000
        })
      } finally {
        this.isLoading = false
      }
    },
    exportScanStock () {
      if (!this.hasStock) return

      this.writeScanStockExport(this.stock)
    },
    exportScanStockGroup (stockRow) {
      const groupDate = this.getStockGroupDate(stockRow)
      if (!groupDate) return
      this.writeScanStockExport(this.stock.filter(stock => this.getStockGroupDate(stock) === groupDate))
    },
    getContextMenuItems (params) {
      const menuItems = (params.defaultItems || []).filter(item => item !== 'export')
      const stockRow = params.node && params.node.group
        ? this.stock.find(stock => this.getStockGroupDate(stock) === params.node.key)
        : params.node && params.node.data

      if (!stockRow) return menuItems

      return [
        ...menuItems,
        'separator',
        {
          name: this.$t('global.export'),
          action: () => this.exportScanStockGroup(stockRow)
        }
      ]
    },
    writeScanStockExport (stockRows) {
      const columns = [
        { header: this.$t('customer.scan_stock.grid.date'), field: 'date' },
        { header: this.$t('customer.scan_stock.grid.barcode'), field: 'barcode' }
      ]
      const data = [
        columns.map(column => column.header),
        ...stockRows.map(stock => columns.map(column => this.formatExportValue(stock[column.field], column.field)))
      ]
      const worksheet = XLSX.utils.aoa_to_sheet(data)
      const workbook = XLSX.utils.book_new()
      const fileDate = new Date().toISOString().slice(0, 10)

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Scan stock')
      XLSX.writeFile(workbook, `GPS-${this.$t('sidebar.scan_stock')}-${fileDate}.csv`)
    },
    getStockGroupDate (stock) {
      if (!stock) return ''
      return this.formatExportValue(stock.date, 'date')
    },
    compareDateFilter (filterLocalDateAtMidnight, cellValue) {
      const cellDate = new Date(cellValue)
      if (Number.isNaN(cellDate.getTime())) return -1

      const cellDateAtMidnight = new Date(cellDate.getFullYear(), cellDate.getMonth(), cellDate.getDate())
      if (cellDateAtMidnight.getTime() === filterLocalDateAtMidnight.getTime()) return 0
      return cellDateAtMidnight < filterLocalDateAtMidnight ? -1 : 1
    },
    formatExportValue (value, field) {
      if (!value) return ''
      if (field !== 'date') return value
      return String(value).split('T')[0]
    }
  }
}
</script>
